import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { fiscalAssessmentDocumentGroupsKind } from "@/features/maintenance/features/workflows/constants/forwardings/steps/fiscalAssessmentEntry/fiscalAssessmentDocumentGroupsKind.constant";
import { useMaintenanceFiscalAssessmentSimpleEntriesAdvance } from "@/features/maintenance/features/workflows/hooks/forwardings/establishmentChoice/fiscalAssessment/simpleEntries/useMaintenanceFiscalAssessmentSimpleEntriesAdvance";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { MaintenanceFiscalAssessmentEntryStatus } from "@/features/maintenance/features/workflows/types/forwardings/fiscalAssessmentEntry/fiscalAssessmentEntry.type";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import FiscalAssessmentDocumentGroupDocument from "./FiscalAssessmentDocumentGroupDocument";
import FiscalAssessmentEntryUpdateDocumentsAppend from "./UpdateDocuments";

interface MaintenanceWorkflowForwardingCheckinEntrySectionProps { }

const MaintenanceWorkflowForwardingFiscalAssessmentEntrySection = ({ }: MaintenanceWorkflowForwardingCheckinEntrySectionProps) => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext();

  const queryClient = useQueryClient();

  const {
    mutate: advance,
    isPending: advanceIsPending,
  } = useMaintenanceFiscalAssessmentSimpleEntriesAdvance({
    fiscalAssessmentEntryId: forwarding.fiscal_assessment_entry?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding',],
        });
      },
    },
  });

  const isAwaitingEstablishmentDocuments = useMemo(() => {
    return forwarding.fiscal_assessment_entry?.status === MaintenanceFiscalAssessmentEntryStatus.AwaitingEstablishmentDocuments;
  }, [forwardingUpdatedAt]);

  const isAwaitingManagementReview = useMemo(() => {
    return forwarding.fiscal_assessment_entry?.status === MaintenanceFiscalAssessmentEntryStatus.AwaitingManagementReview;
  }, [forwardingUpdatedAt]);

  const everyDocumentGroupHasDocument = useMemo(() => {
    return forwarding.fiscal_assessment_entry?.document_groups?.every(
      (documentGroup) => Number(documentGroup.documents?.length) > 0
    );
  }, [forwardingUpdatedAt]);

  return (
    <Box className="p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">
            Recolha de notas
          </p>

          {isAwaitingEstablishmentDocuments && (
            <p className="text-warning text-xs">
              Aguardando envio de notas
            </p>
          )}

          {isAwaitingManagementReview && (
            <p className="text-warning text-xs">
              Aguardando aprovação
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {forwarding?.fiscal_assessment_entry?.document_groups?.map((documentGroup, index) => (
          <React.Fragment key={documentGroup.id}>
            {index > 0 && (
              <div className="h-px bg-white-200" />
            )}

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <p>
                    {fiscalAssessmentDocumentGroupsKind[documentGroup.kind] || documentGroup.kind}
                  </p>
                </div>

                {isAwaitingEstablishmentDocuments && (
                  <FiscalAssessmentEntryUpdateDocumentsAppend
                    documentGroup={documentGroup}
                    disabled={advanceIsPending}
                  />
                )}
              </div>

              {Number(documentGroup.documents.length) > 0 && (
                <div className="flex flex-col xl:grid xl:grid-cols-2 flex-wrap gap-2">
                  {documentGroup.documents.map((document) => (
                    <FiscalAssessmentDocumentGroupDocument
                      key={document.id}
                      documentGroupId={documentGroup.id}
                      document={document}
                      isAwaitingEstablishmentDocuments={isAwaitingEstablishmentDocuments}
                    />
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {isAwaitingEstablishmentDocuments && (
        <div>
          <Button
            className="ml-auto"
            disabled={!everyDocumentGroupHasDocument || advanceIsPending}
            operationKlassToCheckPermission="Establishment::Maintenance::FiscalAssessment::SimpleEntries::Advance"
            onClick={() => advance()}
            isLoading={advanceIsPending}
          >
            Enviar notas
          </Button>
        </div>
      )}
    </Box>
  )
}

export default MaintenanceWorkflowForwardingFiscalAssessmentEntrySection;
